import { Component, type ErrorInfo, type ReactNode } from 'react';
import type { ExceptionReporter, SafeExceptionContext } from '../lib/exception-reporting';

export interface ExceptionBoundaryProps {
  children?: ReactNode;
  reporter?: ExceptionReporter;
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  context?: SafeExceptionContext | (() => SafeExceptionContext);
}

export interface ExceptionBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ExceptionBoundary extends Component<ExceptionBoundaryProps, ExceptionBoundaryState> {
  public override state: ExceptionBoundaryState = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): ExceptionBoundaryState {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const baseContext =
      typeof this.props.context === 'function' ? this.props.context() : this.props.context;
    const context: SafeExceptionContext = {
      ...baseContext,
      componentStack: errorInfo.componentStack ?? undefined,
    };

    if (this.props.reporter) {
      try {
        const reportPromise = this.props.reporter.report(error, context);
        if (reportPromise && typeof reportPromise.catch === 'function') {
          reportPromise.catch(() => {
            // contained
          });
        }
      } catch {
        // contained
      }
    }

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  public reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  public override render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback(this.state.error, this.reset);
      }
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      return null;
    }

    return this.props.children;
  }
}
